import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skuGlyph } from '../../lib/sku-glyph';
import { useSkuLabel } from '../../lib/sku-i18n';
import { formatShortDate } from '../../lib/format-date';
import type { PurchaseItem } from '../../hooks/useMyPurchases';
import styles from './PurchaseTile.module.css';

export interface PurchaseTileProps {
  item: PurchaseItem;
  onClick: () => void;
  /** When provided, renders the ✕ delete overlay (Purchases page only). */
  onDelete?: () => void;
  deleteAriaLabel?: string;
  cardTestId?: string;
  deleteTestId?: string;
}

/**
 * One purchase as a uniform tile: real result image when the purchase produced
 * one (thumbUrl), otherwise an icon tile (SKU glyph + optional teaser). Used by
 * both the Me-overview rites strip and the Purchases page so they stay identical.
 */
export function PurchaseTile({
  item,
  onClick,
  onDelete,
  deleteAriaLabel,
  cardTestId,
  deleteTestId,
}: PurchaseTileProps): JSX.Element {
  const { i18n } = useTranslation();
  const skuLabel = useSkuLabel();
  const [broken, setBroken] = useState(false);
  const showImg = Boolean(item.thumbUrl) && !broken;

  return (
    <div className={styles.wrapper}>
      <button type="button" className={styles.card} onClick={onClick} data-testid={cardTestId}>
        <div className={styles.face}>
          {showImg ? (
            <img
              src={item.thumbUrl ?? undefined}
              alt=""
              className={styles.img}
              loading="lazy"
              onError={() => setBroken(true)}
            />
          ) : (
            <div className={styles.iconTile}>
              <span className={styles.glyph} aria-hidden="true">{skuGlyph(item.skuCode)}</span>
              {item.teaserText ? <span className={styles.teaser}>{item.teaserText}</span> : null}
            </div>
          )}
        </div>
        <div className={styles.label}>{skuLabel(item.skuName)}</div>
        <div className={styles.date}>{formatShortDate(item.createdAt, i18n.language)}</div>
      </button>
      {onDelete ? (
        <button
          type="button"
          className={styles.deleteBtn}
          aria-label={deleteAriaLabel}
          data-testid={deleteTestId}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}
